/**
 * Teams, squads and formations — the front end's roster data. (§8.2, §9.1, §16)
 *
 * ── The licensing rule this file exists to obey ─────────────────────────────
 *
 * SESSION_LOG (2026-07-17 user decision) is explicit: NO real player names,
 * faces or club branding, and near-miss namealikes were REJECTED as still
 * infringing. So every club, every nation and every player below is invented,
 * and the "crest" is generated from the team's own two kit colours rather than
 * being a badge asset. Nothing here should ever be replaced with a real name.
 *
 * ── Why squads are generated, not authored ──────────────────────────────────
 *
 * 16 clubs + 32 nations × 20 players is 960 players. Hand-authoring that is a
 * 30 KB data file nobody will maintain, so squads are DERIVED from a seeded
 * PRNG keyed on the team id: `squadFor(id)` returns the same 20 players, with
 * the same names, ratings and shirt numbers, on every call and after every page
 * reload — without persisting a single byte. That determinism is what lets the
 * squad screen store a lineup as a list of player IDS (see tournament-store.ts)
 * instead of a copy of the whole roster.
 *
 * ── Positions are the sim's, never a parallel set ──────────────────────────
 *
 * Every position below is a `Position` from @fg/shared/sim (§8.2's vocabulary:
 * GK/CB/LB/RB/LWB/RWB/CDM/CM/CAM/LM/RM/LW/RW/CF/ST) and every pitch coordinate
 * comes from `POSITION_ANCHORS` — the SAME table the AI's Layer-3 formation
 * solver and the rules FSM's kickoff staging read (sim/formation-anchors.ts).
 * If a formation preview here disagrees with where the AI actually stands, the
 * anchors changed and this file inherits the fix for free.
 *
 * This module is pure: no DOM, no storage, no three.js. It is unit-tested in
 * node (teams.test.ts).
 */

import type { Position } from '@fg/shared/sim';
// Deep subpath into @fg/shared/ai — the anchors live in sim/formation-anchors.ts
// (re-exported by ai/formations.ts) and neither `@fg/shared/sim` nor
// `@fg/shared/ai` re-exports them from its index. sim/** is under lint-enforced
// purity rules and must not be edited for a UI need, so shared's package.json
// gained an `"./ai/formations"` export entry instead. Read-only use.
import { POSITION_ANCHORS, isDefenderPosition, isForwardPosition } from '@fg/shared/ai/formations';
// The seeded RNG lives with the competition maths (the other thing that needs
// reproducible randomness). One-way dependency: teams → competition, never back.
import { createRng, hashString } from './competition.js';
// The cast is DATA, generated by `scripts/freeze-cast-manifest.mjs` from the art
// pipeline's own variant configs. Importing it here is what stops the two
// modelled clubs from having a second, invented squad — see `squadFor`.
import { castSquad } from '../render/player/castManifest.js';
import { KICKOFF_SPAWN_ANCHORS } from '@fg/shared/sim';

/**
 * Competition names. Invented, for the same reason the clubs are: "World Cup"
 * and every real league name belong to somebody. Change these two strings and
 * every screen follows — they are not repeated anywhere else.
 */
export const LEAGUE_NAME = 'ARENA LEAGUE';
export const CUP_NAME = 'ARENA WORLD CUP';

export type TeamKind = 'club' | 'nation';

export interface Team {
  /** Stable storage key. Namespaced by kind so a club and a nation never collide. */
  id: string;
  name: string;
  /** 3-letter code — the crest text and every space-constrained table cell. */
  short: string;
  /** Invented home city (clubs) or continental zone (nations). */
  subtitle: string;
  kind: TeamKind;
  /** Kit colours, CSS hex. `primary` is the crest fill, `secondary` its text. */
  primary: string;
  secondary: string;
  /** 60–90. Drives squad ratings and the fixture simulator's odds. */
  rating: number;
  /**
   * ISO 3166-1 alpha-2 (lowercase) for nations, resolving to
   * `/ui/flags/<code>.svg`. Clubs have none — they are invented, so there is no
   * real flag to draw and a crest is generated from initials instead.
   *
   * `gb-eng` is the one non-ISO code here; it is the flag-icons key for
   * England, which competes as its own football nation.
   */
  flag?: string;
}

export interface SquadPlayer {
  /** `${teamId}#${slot}` — stable across reloads because squads are derived. */
  id: string;
  name: string;
  position: Position;
  /** 45–94, clustered around the team rating. */
  rating: number;
  age: number;
  shirt: number;
  /**
   * The cast character this squad member is drawn as, when he is one of the
   * sixteen modelled players of a modelled club. Undefined everywhere else,
   * which is what tells the renderer to fall back to the shared master.
   */
  castId?: string;
}

export interface Formation {
  id: string;
  /** '4-4-2' — a shape, not a competition or club, so nothing to license. */
  name: string;
  /** Exactly 11 sim `Position` codes, index = on-pitch slot. */
  slots: readonly Position[];
}

// ---------------------------------------------------------------------------
// Clubs and nations (all invented — see the header)
// ---------------------------------------------------------------------------

/**
 * 16 clubs = a 30-matchday double round-robin, which is a season a player can
 * actually finish in a browser game while still producing a league table that
 * reads like one. Ratings are spread 70–86 so the table has a real hierarchy
 * and picking the bottom club is a genuinely harder career.
 */
export const CLUBS: readonly Team[] = [
  mkClub('northgate', 'Northgate United', 'NGU', 'Northgate', '#c0392b', '#ffffff', 85),
  mkClub('riverfall', 'Riverfall FC', 'RVF', 'Riverfall', '#1f6fb2', '#ffffff', 84),
  mkClub('ironvale', 'Ironvale Athletic', 'IVA', 'Ironvale', '#2c3e50', '#f1c40f', 82),
  mkClub('solport', 'Solport City', 'SPC', 'Solport', '#e67e22', '#1b1b1b', 81),
  mkClub('kestrel', 'Kestrel Rovers', 'KRV', 'Kestrel Hill', '#8e44ad', '#ffffff', 79),
  mkClub('havenbrook', 'Havenbrook Town', 'HBT', 'Havenbrook', '#16a085', '#04231e', 78),
  mkClub('ashcombe', 'Ashcombe Wanderers', 'ASW', 'Ashcombe', '#7f8c8d', '#101418', 77),
  mkClub('verdano', 'Verdano Sporting', 'VDS', 'Verdano', '#27ae60', '#ffffff', 77),
  mkClub('grimhaven', 'Grimhaven Albion', 'GHA', 'Grimhaven', '#34495e', '#9bd7ff', 76),
  mkClub('lakemoor', 'Lakemoor Dynamo', 'LKD', 'Lakemoor', '#2980b9', '#f7e04a', 75),
  mkClub('stonebridge', 'Stonebridge Union', 'STB', 'Stonebridge', '#95a5a6', '#1b2430', 74),
  mkClub('cobaltbay', 'Cobalt Bay FC', 'CBB', 'Cobalt Bay', '#1a5fa8', '#c8e6ff', 74),
  mkClub('thornfield', 'Thornfield Foxes', 'THF', 'Thornfield', '#b8860b', '#22180a', 73),
  mkClub('marisco', 'Marisco Deportivo', 'MRD', 'Marisco', '#d64541', '#fff3d0', 72),
  mkClub('vantwerp', 'Vantwerp Olympique', 'VTO', 'Vantwerp', '#0f7864', '#ffe9a8', 71),
  mkClub('pinehurst', 'Pinehurst Saints', 'PHS', 'Pinehurst', '#4b6584', '#ffffff', 70),
  // ── THE TWO MODELLED CLUBS ────────────────────────────────────────────────
  // Every club above is a name, a colour and a synthesized squad. These two are
  // the only ones whose players EXIST as characters: sixteen built models each,
  // with the shirt number and the kit painted into the model's own textures by
  // the art pipeline. `squadFor` returns their real cast below rather than
  // generating one, so the squad screen, the commentary booth and the renderer
  // are all reading the same sixteen men.
  mkClub('arena', 'Footy Arena FC', 'ARN', 'Arena Park', '#111418', '#d4af37', 84),
  mkClub('rivals', 'Rivals United', 'RVU', 'Rivals Ground', '#123c8c', '#f2f4f8', 83),
];

/**
 * Club id ↔ the `club` field in the cast's variant configs.
 *
 * The art pipeline says `arena`/`rivals`; the UI says `club:arena`/`club:rivals`.
 * One table, so neither side has to know the other's spelling.
 */
export const CAST_CLUB_TEAM_ID: Readonly<Record<string, string>> = {
  arena: 'club:arena',
  rivals: 'club:rivals',
};

export const TEAM_ID_CAST_CLUB: Readonly<Record<string, string>> = {
  'club:arena': 'arena',
  'club:rivals': 'rivals',
};

/**
 * The two sides a match is played between when nobody has chosen otherwise.
 *
 * Team INDEX 0 is home and 1 is away everywhere in the sim (`PlayerId =
 * team * squadSize + slot`), and `deriveKit` already colours them red and blue
 * by that index. Nothing carries a club id into the renderer today — see
 * `castRegistry.ts` — so this pair is what the cast path assumes until a match
 * says otherwise.
 */
export const DEFAULT_MATCH_TEAM_IDS: readonly [string, string] = ['club:arena', 'club:rivals'];

/**
 * The tournament is 8 groups of 4 → 16 qualifiers → R16/QF/SF/FINAL, the shape
 * the knockout code in competition.ts is written for. That needs a FIELD of 32,
 * which is not the same thing as a POOL of 32: this list is the pool a player
 * chooses from, and startWorldCup qualifies 32 of them. Keeping the two numbers
 * separate is what lets a nation be pickable without being a permanent seed.
 *
 * These are REAL country names with REAL flags, which is deliberate and is the
 * one place this project uses real-world identity: a World Cup mode with
 * invented countries reads as a different game. What is NOT used is anything
 * owned by a federation — no crests, no official kits, no tournament marks, no
 * real players. `subtitle` is the confederation, which is a fact about the
 * country, not a logo. Squads stay fully synthetic (see squadFor).
 *
 * `primary`/`secondary` are drawn from each flag's own palette so the kit and
 * the flag agree without copying a manufactured shirt design.
 *
 * Flags are verified SVGs from flag-icons (MIT) — see
 * docs/THIRD_PARTY_NOTICES.md. They are NOT drawn or generated, because an
 * approximated national flag is worse than none.
 */
export const NATIONS: readonly Team[] = [
  mkNation('argentina', 'Argentina', 'ARG', 'CONMEBOL', '#75AADB', '#FFFFFF', 90, 'ar'),
  mkNation('france', 'France', 'FRA', 'UEFA', '#1B3A8C', '#FFFFFF', 90, 'fr'),
  mkNation('brazil', 'Brazil', 'BRA', 'CONMEBOL', '#F7D117', '#046A38', 89, 'br'),
  mkNation('spain', 'Spain', 'ESP', 'UEFA', '#AA151B', '#F1BF00', 88, 'es'),
  mkNation('england', 'England', 'ENG', 'UEFA', '#FFFFFF', '#CE1124', 88, 'gb-eng'),
  mkNation('portugal', 'Portugal', 'POR', 'UEFA', '#046A38', '#DA291C', 87, 'pt'),
  mkNation('germany', 'Germany', 'GER', 'UEFA', '#111111', '#DD0000', 86, 'de'),
  mkNation('netherlands', 'Netherlands', 'NED', 'UEFA', '#F36C21', '#21468B', 85, 'nl'),
  mkNation('italy', 'Italy', 'ITA', 'UEFA', '#0C4DA2', '#FFFFFF', 84, 'it'),
  mkNation('belgium', 'Belgium', 'BEL', 'UEFA', '#C8102E', '#FDDA24', 83, 'be'),
  mkNation('croatia', 'Croatia', 'CRO', 'UEFA', '#FF0000', '#FFFFFF', 82, 'hr'),
  mkNation('morocco', 'Morocco', 'MAR', 'CAF', '#C1272D', '#006233', 82, 'ma'),
  mkNation('uruguay', 'Uruguay', 'URU', 'CONMEBOL', '#5CB8E6', '#FFFFFF', 82, 'uy'),
  mkNation('japan', 'Japan', 'JPN', 'AFC', '#12225B', '#FFFFFF', 82, 'jp'),
  mkNation('colombia', 'Colombia', 'COL', 'CONMEBOL', '#FCD116', '#003893', 81, 'co'),
  mkNation('denmark', 'Denmark', 'DEN', 'UEFA', '#C60C30', '#FFFFFF', 80, 'dk'),
  mkNation('senegal', 'Senegal', 'SEN', 'CAF', '#00853F', '#FDEF42', 80, 'sn'),
  mkNation('switzerland', 'Switzerland', 'SUI', 'UEFA', '#D52B1E', '#FFFFFF', 79, 'ch'),
  mkNation('southkorea', 'South Korea', 'KOR', 'AFC', '#0B3B8C', '#CD2E3A', 79, 'kr'),
  mkNation('unitedstates', 'United States', 'USA', 'CONCACAF', '#3C3B6E', '#B22234', 78, 'us'),
  mkNation('mexico', 'Mexico', 'MEX', 'CONCACAF', '#006847', '#CE1126', 78, 'mx'),
  mkNation('serbia', 'Serbia', 'SRB', 'UEFA', '#C6363C', '#0C4076', 77, 'rs'),
  mkNation('ecuador', 'Ecuador', 'ECU', 'CONMEBOL', '#FFDD00', '#034EA2', 77, 'ec'),
  mkNation('poland', 'Poland', 'POL', 'UEFA', '#FFFFFF', '#DC143C', 76, 'pl'),
  mkNation('egypt', 'Egypt', 'EGY', 'CAF', '#CE1126', '#FFFFFF', 76, 'eg'),
  mkNation('nigeria', 'Nigeria', 'NGA', 'CAF', '#008751', '#FFFFFF', 76, 'ng'),
  mkNation('iran', 'Iran', 'IRN', 'AFC', '#239F40', '#DA0000', 76, 'ir'),
  mkNation('cameroon', 'Cameroon', 'CMR', 'CAF', '#007A5E', '#FCD116', 75, 'cm'),
  mkNation('canada', 'Canada', 'CAN', 'CONCACAF', '#FF0000', '#FFFFFF', 75, 'ca'),
  mkNation('ghana', 'Ghana', 'GHA', 'CAF', '#006B3F', '#FCD116', 74, 'gh'),
  mkNation('saudiarabia', 'Saudi Arabia', 'KSA', 'AFC', '#006C35', '#FFFFFF', 73, 'sa'),
  // Rated below the rest on purpose. India have never reached a World Cup, so
  // seeding them mid-table would be a lie about the sport; bottom of the list
  // makes picking them the hard run it should be, and qualification is
  // guaranteed for whoever you choose (startWorldCup), so the rating costs you
  // nothing but the difficulty you signed up for. Sky blue is the Blue Tigers'
  // own colour; the orange is off the flag, not off a manufactured shirt.
  // India took Costa Rica's place rather than extending the list, on the
  // owner's call — a pool of exactly 32 means nobody sits a tournament out.
  mkNation('india', 'India', 'IND', 'AFC', '#0099DC', '#FF9933', 66, 'in'),
];

function mkClub(
  id: string, name: string, short: string, subtitle: string,
  primary: string, secondary: string, rating: number,
): Team {
  return { id: `club:${id}`, name, short, subtitle, kind: 'club', primary, secondary, rating };
}

function mkNation(
  id: string,
  name: string,
  short: string,
  subtitle: string,
  primary: string,
  secondary: string,
  rating: number,
  flag: string,
): Team {
  return { id: `nation:${id}`, name, short, subtitle, kind: 'nation', primary, secondary, rating, flag };
}

const TEAM_INDEX = new Map<string, Team>(
  [...CLUBS, ...NATIONS].map((t) => [t.id, t]),
);

export function teamById(id: string): Team | undefined {
  return TEAM_INDEX.get(id);
}

/** Never-undefined lookup for render paths — a missing id yields a grey stub so
 *  a stale persisted career (renamed club) degrades instead of throwing. */
export function teamOrStub(id: string): Team {
  return (
    TEAM_INDEX.get(id) ?? {
      id, name: id.replace(/^[a-z]+:/, '').toUpperCase(), short: '???',
      subtitle: 'UNKNOWN', kind: 'club', primary: '#3a4353', secondary: '#ffffff', rating: 70,
    }
  );
}

export function teamsOfKind(kind: TeamKind): readonly Team[] {
  return kind === 'club' ? CLUBS : NATIONS;
}

// ---------------------------------------------------------------------------
// Formations
// ---------------------------------------------------------------------------

/**
 * Named shapes, every slot a real §8.2 `Position`. The roster IS the formation
 * (ai/formations.ts header), so choosing '4-3-3' here is exactly the same kind
 * of statement the AI already understands — no new concept was introduced.
 */
export const FORMATIONS: readonly Formation[] = [
  { id: '442', name: '4-4-2', slots: ['GK', 'RB', 'CB', 'CB', 'LB', 'RM', 'CM', 'CM', 'LM', 'ST', 'ST'] },
  { id: '433', name: '4-3-3', slots: ['GK', 'RB', 'CB', 'CB', 'LB', 'CDM', 'CM', 'CM', 'RW', 'ST', 'LW'] },
  { id: '4231', name: '4-2-3-1', slots: ['GK', 'RB', 'CB', 'CB', 'LB', 'CDM', 'CDM', 'RW', 'CAM', 'LW', 'ST'] },
  { id: '352', name: '3-5-2', slots: ['GK', 'CB', 'CB', 'CB', 'RWB', 'CM', 'CDM', 'CM', 'LWB', 'ST', 'ST'] },
  { id: '532', name: '5-3-2', slots: ['GK', 'RWB', 'CB', 'CB', 'CB', 'LWB', 'CM', 'CDM', 'CM', 'ST', 'ST'] },
  { id: '4141', name: '4-1-4-1', slots: ['GK', 'RB', 'CB', 'CB', 'LB', 'CDM', 'RM', 'CM', 'CM', 'LM', 'ST'] },
];

export const DEFAULT_FORMATION_ID = '442';

export function formationById(id: string): Formation {
  return FORMATIONS.find((f) => f.id === id) ?? FORMATIONS[0]!;
}

/**
 * How far apart players sharing one position sit, in normalized cross-pitch
 * units (|fz| ∈ [0,1] where 1 = touchline). 0.40 × 34 m half-width ≈ 13.6 m, so
 * a centre-back pair sits ~14 m apart and a back three spans ~27 m — the same
 * job practice.ts's SPAWN_DUPLICATE_SPREAD does for kickoff spawns, but sized
 * for a legible diagram rather than a Law-8 legal spawn.
 */
const DUPLICATE_SPREAD = 0.4;

export interface FormationSpot {
  /** -1 = own goal line, +1 = opponent goal line (POSITION_ANCHORS' axis). */
  fx: number;
  /** -1 = left touchline, +1 = right touchline, after duplicate spreading. */
  fz: number;
}

/**
 * Where each of the 11 slots stands, from the sim's own anchors.
 *
 * Duplicates are CENTRED on the anchor rather than fanned one way: with n
 * players on a position the i-th gets `(i - (n-1)/2) * SPREAD`, so a pair
 * straddles the anchor symmetrically (±0.20) and a trio is left/centre/right.
 * Fanning from the anchor (the naive +s, -s, +2s…) puts a centre-back pair at
 * 0 and +0.4, which draws a visibly lopsided back four.
 */
export function formationSpots(formation: Formation): FormationSpot[] {
  const total = new Map<Position, number>();
  for (const pos of formation.slots) total.set(pos, (total.get(pos) ?? 0) + 1);
  const seen = new Map<Position, number>();
  return formation.slots.map((pos) => {
    const anchor = POSITION_ANCHORS[pos];
    const n = total.get(pos) ?? 1;
    const i = seen.get(pos) ?? 0;
    seen.set(pos, i + 1);
    const offset = n <= 1 ? 0 : (i - (n - 1) / 2) * DUPLICATE_SPREAD;
    return { fx: anchor.fx, fz: clamp(anchor.fz + offset, -0.95, 0.95) };
  });
}

export type PositionGroup = 'GK' | 'DEF' | 'MID' | 'FWD';

/** Grouping for pill colours and squad-list sorting. Uses the shared predicates
 *  (ai/formations.ts) so "is a wing-back a defender?" is answered in ONE place —
 *  note `isDefenderPosition` counts GK, hence the GK test first. */
export function positionGroup(pos: Position): PositionGroup {
  if (pos === 'GK') return 'GK';
  if (isDefenderPosition(pos)) return 'DEF';
  if (isForwardPosition(pos)) return 'FWD';
  return 'MID';
}

const GROUP_ORDER: Record<PositionGroup, number> = { GK: 0, DEF: 1, MID: 2, FWD: 3 };

export function positionGroupRank(pos: Position): number {
  return GROUP_ORDER[positionGroup(pos)];
}

// ---------------------------------------------------------------------------
// Deterministic squad generation
// ---------------------------------------------------------------------------

/**
 * The 20-man squad shape. Covers every position any FORMATIONS entry asks for
 * (including the wing-backs 3-5-2/5-3-2 need) plus cover, so auto-selection
 * never has to put a striker at left-back.
 */
const SQUAD_TEMPLATE: readonly Position[] = [
  'GK', 'GK',
  'RB', 'RWB', 'CB', 'CB', 'CB', 'LB', 'LWB',
  'CDM', 'CDM', 'CM', 'CM', 'CAM', 'RM', 'LM',
  'RW', 'LW', 'ST', 'ST',
];

/**
 * Invented given names. Deliberately ordinary and international-sounding, and
 * paired below with SYNTHESIZED surnames so no combination can land on a real
 * footballer (SESSION_LOG: namealikes were rejected outright).
 */
const FIRST_NAMES: readonly string[] = [
  'Aaron', 'Bruno', 'Caleb', 'Dario', 'Elias', 'Finn', 'Gabriel', 'Hugo',
  'Ivan', 'Jonas', 'Kai', 'Leon', 'Mateo', 'Nico', 'Omar', 'Pieter',
  'Quinn', 'Rafael', 'Samir', 'Tobias', 'Umar', 'Viktor', 'Wesley', 'Yannick',
  'Zane', 'Andres', 'Bilal', 'Cesar', 'Dmitri', 'Ercan', 'Fabio', 'Gustav',
  'Henrik', 'Ilias', 'Joel', 'Kenji', 'Lars', 'Marek', 'Noah', 'Oscar',
];

/** Surnames are BUILT from these two pools rather than listed, which guarantees
 *  they are coinages: 30 × 20 = 600 synthetic surnames, none of them a person. */
const SURNAME_HEADS: readonly string[] = [
  'Ald', 'Bram', 'Cade', 'Dorn', 'Elve', 'Farn', 'Gale', 'Hald', 'Irve', 'Jarl',
  'Kell', 'Lund', 'Marn', 'Nord', 'Orle', 'Pren', 'Quil', 'Rask', 'Sarn', 'Tove',
  'Vane', 'Wold', 'Yarn', 'Zeld', 'Brack', 'Corve', 'Drev', 'Ember', 'Fjell', 'Halv',
];
const SURNAME_TAILS: readonly string[] = [
  'son', 'wick', 'ford', 'holm', 'mann', 'ez', 'ic', 'ov', 'aldi', 'eiro',
  'ard', 'ley', 'strom', 'berg', 'ing', 'dahl', 'quist', 'ner', 'sen', 'ari',
];

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Cast position codes → the sim's §8.2 codes.
 *
 * The art roster writes RCB/LCB because a squad list does; the sim has one CB.
 * Anything unrecognised becomes CM rather than throwing — a new position code
 * in a variant config must not stop a match from kicking off.
 */
const CAST_POSITION_ALIAS: Readonly<Record<string, Position>> = {
  RCB: 'CB', LCB: 'CB', CDM: 'CDM', CAM: 'CAM', STAFF: 'CM',
};

function simPosition(code: string): Position {
  const aliased = CAST_POSITION_ALIAS[code];
  if (aliased) return aliased;
  return KICKOFF_SPAWN_ANCHORS[code as Position] === undefined ? 'CM' : (code as Position);
}

const SQUAD_CACHE = new Map<string, readonly SquadPlayer[]>();

/**
 * The team's 20-man squad. Memoized because the squad screen re-reads it on
 * every repaint and regeneration is pure waste, not because it is expensive.
 */
export function squadFor(teamId: string): readonly SquadPlayer[] {
  const cached = SQUAD_CACHE.get(teamId);
  if (cached) return cached;
  const team = teamOrStub(teamId);
  const rng = createRng(hashString(teamId));

  // ── the two modelled clubs use their real cast ────────────────────────────
  // Their names, shirt numbers and positions are not generated: they are what
  // the art pipeline built into the models, and the number on the back of the
  // shirt is a texture, not a label the UI can change its mind about. Ratings
  // and ages stay synthetic off the same seeded stream, so the rest of the game
  // (auto-lineup, competition simulation, the squad screen) is unchanged.
  const castClub = TEAM_ID_CAST_CLUB[teamId];
  if (castClub) {
    const cast = castSquad(castClub);
    if (cast.length) {
      const squad: SquadPlayer[] = cast.map((c, slot) => {
        const starterBonus = c.starter ? 3 : 0;
        // The art roster carries a SURNAME ONLY, in caps ("BRENNAN") — that is
        // what goes on the back of a shirt, so it is what the variant configs
        // record. Every other squad in the game is "Given Surname", and there
        // is a structural test enforcing it as the licensing rule. Wiring the
        // cast in verbatim gave the two modelled clubs one-word players and
        // broke that rule for all 32 of them. So: keep the modelled surname
        // (it is painted into the kit and spoken by the commentary booth), and
        // draw the given name off the SAME seeded stream every other club uses,
        // so it is stable across runs and across reloads.
        const given = FIRST_NAMES[Math.floor(rng() * FIRST_NAMES.length)] ?? 'Alex';
        const surname = c.name.charAt(0) + c.name.slice(1).toLowerCase();
        return {
          id: `${teamId}#${slot}`,
          name: `${given} ${surname}`,
          position: simPosition(c.position),
          rating: clamp(Math.round(team.rating + starterBonus + (rng() * 14 - 8)), 45, 94),
          age: 18 + Math.floor(rng() * 19),
          shirt: c.number,
          castId: c.id,
        };
      });
      SQUAD_CACHE.set(teamId, squad);
      return squad;
    }
  }

  const squad: SquadPlayer[] = SQUAD_TEMPLATE.map((position, slot) => {
    const first = FIRST_NAMES[Math.floor(rng() * FIRST_NAMES.length)] ?? 'Alex';
    const head = SURNAME_HEADS[Math.floor(rng() * SURNAME_HEADS.length)] ?? 'Ald';
    const tail = SURNAME_TAILS[Math.floor(rng() * SURNAME_TAILS.length)] ?? 'son';
    // The first eleven template slots are the intended starters, so weight them
    // up: a squad whose bench outrates its XI makes auto-selection look broken.
    const starterBonus = slot < 11 ? 3 : 0;
    const rating = clamp(Math.round(team.rating + starterBonus + (rng() * 14 - 8)), 45, 94);
    return {
      id: `${teamId}#${slot}`,
      name: `${first} ${head}${tail}`,
      position,
      rating,
      age: 18 + Math.floor(rng() * 19),
      shirt: slot + 1,
    };
  });
  SQUAD_CACHE.set(teamId, squad);
  return squad;
}

export function playerById(teamId: string, playerId: string): SquadPlayer | undefined {
  return squadFor(teamId).find((p) => p.id === playerId);
}

/**
 * Positional fit, 0 (wrong) … 3 (exact). Used by auto-selection and by the
 * squad screen to grey out a nonsense assignment without forbidding it — a
 * manager is allowed to play a winger at left-back, they just see it is a
 * stretch.
 */
export function positionFit(playerPos: Position, slotPos: Position): 0 | 1 | 2 | 3 {
  if (playerPos === slotPos) return 3;
  const pg = positionGroup(playerPos);
  const sg = positionGroup(slotPos);
  // A keeper is never a fit anywhere else, and nobody else is a fit in goal.
  if (pg === 'GK' || sg === 'GK') return 0;
  if (pg === sg) return 2;
  // Neighbouring bands (DEF↔MID, MID↔FWD) are a stretch, not nonsense.
  const dist = Math.abs(GROUP_ORDER[pg] - GROUP_ORDER[sg]);
  return dist === 1 ? 1 : 0;
}

/**
 * Auto-pick a starting XI for a formation: best fit first, rating as the
 * tiebreak, each player used once. Greedy over slots in template order (GK
 * first, then defence → attack), which is what a manager does and is stable —
 * the same team + formation always yields the same XI, so a reload never
 * silently reshuffles a lineup the player never edited.
 */
export function autoLineup(teamId: string, formationId: string): string[] {
  const formation = formationById(formationId);
  const squad = squadFor(teamId);
  const used = new Set<string>();
  return formation.slots.map((slotPos) => {
    let best: SquadPlayer | null = null;
    let bestScore = -1;
    for (const p of squad) {
      if (used.has(p.id)) continue;
      const score = positionFit(p.position, slotPos) * 1000 + p.rating;
      if (score > bestScore) {
        bestScore = score;
        best = p;
      }
    }
    if (best) used.add(best.id);
    return best?.id ?? '';
  });
}

/** Average rating of an XI — the squad screen's headline number. */
export function lineupRating(teamId: string, lineup: readonly string[]): number {
  const squad = squadFor(teamId);
  let sum = 0;
  let n = 0;
  for (const id of lineup) {
    const p = squad.find((q) => q.id === id);
    if (!p) continue;
    sum += p.rating;
    n++;
  }
  return n === 0 ? 0 : Math.round(sum / n);
}
